import { api } from "@/shared/api";

const STORAGE_KEY = "polaris-push-device-v1";
type DevicePreferences = { userId: string; workspaces: string[] };

export class PushPreferenceError extends Error {
  constructor() {
    super(
      "Il browser non permette di salvare le preferenze. Consenti l'archiviazione per Polaris e riprova: le notifiche non sono state attivate.",
    );
    this.name = "PushPreferenceError";
  }
}

/** Run synchronously before the permission prompt so that the original click remains active. */
export function requirePushStorage() {
  try {
    const probe = `${STORAGE_KEY}-probe`;
    localStorage.setItem(probe, "1");
    localStorage.removeItem(probe);
  } catch {
    throw new PushPreferenceError();
  }
}

export function requestPushPermission(): Promise<NotificationPermission> {
  requirePushStorage();
  return Notification.permission === "default"
    ? Notification.requestPermission()
    : Promise.resolve(Notification.permission);
}

export function pushSupported() {
  return (
    window.isSecureContext &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export function readPushPreferences(): DevicePreferences | null {
  try {
    const value: unknown = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "null");
    if (
      !value ||
      typeof value !== "object" ||
      !("userId" in value) ||
      typeof value.userId !== "string" ||
      !("workspaces" in value) ||
      !Array.isArray(value.workspaces)
    )
      return null;
    return {
      userId: value.userId,
      workspaces: value.workspaces.filter(
        (slug): slug is string =>
          typeof slug === "string" && /^[a-z0-9][a-z0-9-]{0,62}$/.test(slug),
      ),
    };
  } catch {
    return null;
  }
}

export function rememberPushWorkspace(userId: string, slug: string, enabled: boolean) {
  const existing = readPushPreferences();
  const workspaces = existing?.userId === userId ? existing.workspaces : [];
  const next = enabled
    ? [...new Set([...workspaces, slug])]
    : workspaces.filter((item) => item !== slug);
  // Only the device opt-in belongs in storage. Never save an endpoint, key, message or lead.
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ userId, workspaces: next }));
  } catch {
    throw new PushPreferenceError();
  }
}

/** Persist the opt-in before the server can activate it. */
export async function savePushSubscription(
  userId: string,
  slug: string,
  subscription: PushSubscription,
) {
  const previous = readPushPreferences();
  rememberPushWorkspace(userId, slug, true);
  try {
    await api.push.subscribe(slug, subscription.toJSON());
  } catch (error) {
    try {
      if (previous) localStorage.setItem(STORAGE_KEY, JSON.stringify(previous));
      else localStorage.removeItem(STORAGE_KEY);
    } catch {
      // The browser endpoint is discarded by the caller; stale metadata cannot re-enable it.
    }
    throw error;
  }
}

/** After server revocation, a storage failure also discards the device endpoint to prevent re-enabling. */
export async function forgetPushWorkspace(
  userId: string,
  slug: string,
  subscription: PushSubscription,
) {
  let storageAvailable = true;
  try {
    rememberPushWorkspace(userId, slug, false);
  } catch {
    storageAvailable = false;
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* Browser storage is unavailable. */
    }
  }
  if (!storageAvailable || (readPushPreferences()?.workspaces.length ?? 0) === 0) {
    if (!(await subscription.unsubscribe())) {
      throw new Error("Riprova a scollegare il dispositivo dalle notifiche.");
    }
  }
  return storageAvailable;
}

export function applicationServerKey(value: string): Uint8Array<ArrayBuffer> {
  const padding = "=".repeat((4 - (value.length % 4)) % 4);
  const raw = atob(`${value}${padding}`.replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from(raw, (character) => character.charCodeAt(0));
}

export function subscriptionMatchesKey(subscription: PushSubscription, publicKey: string) {
  const stored = subscription.options.applicationServerKey;
  if (!stored) return false;
  const expected = applicationServerKey(publicKey);
  const actual = new Uint8Array(stored);
  return (
    actual.length === expected.length && actual.every((value, index) => value === expected[index])
  );
}

export async function discardPreviousAccount(
  userId: string,
  registration: ServiceWorkerRegistration,
) {
  const existing = readPushPreferences();
  if (existing?.userId === userId) return;
  const subscription = await registration.pushManager.getSubscription();
  if (subscription && !(await subscription.unsubscribe())) {
    throw new Error("Non riusciamo a scollegare le notifiche dell'account precedente. Riprova.");
  }
  localStorage.removeItem(STORAGE_KEY);
}

/** Revoke the browser endpoint before ending the session, even when API cleanup fails. */
export async function revokePushBeforeSignOut(userId: string) {
  if (!pushSupported()) return;
  const registration = await navigator.serviceWorker.getRegistration("/");
  const subscription = await registration?.pushManager.getSubscription();
  const preferences = readPushPreferences();
  if (subscription) {
    const endpoint = subscription.endpoint;
    if (!(await subscription.unsubscribe())) {
      throw new Error("Non riusciamo a disattivare le notifiche su questo dispositivo. Riprova.");
    }
    if (preferences?.userId === userId) {
      await Promise.allSettled(
        preferences.workspaces.map((slug) => api.push.revoke(slug, endpoint)),
      );
    }
  }
  localStorage.removeItem(STORAGE_KEY);
}
